import { isArray } from 'lodash';
import {
    PartialPreconClientSettings,
    mergeClientSettings,
} from './merge-settings';
import {
    PreconClientSettingsJson,
    getSettingsJson,
    resolveSettingsJson,
} from './settings-json';

export const PRECON_CLIENT_SETTINGS_STORAGE_KEY = 'preconClientSettings';

export function getStorageClientSettings(
    storage: Storage,
    storageKey = PRECON_CLIENT_SETTINGS_STORAGE_KEY,
    strict = false
): PartialPreconClientSettings {
    const value = storage.getItem(storageKey);

    if (typeof value === 'string') {
        try {
            const settings = JSON.parse(value);

            if (!settings) return {};

            if (isArray(settings))
                throw new Error('invalid-precon-client-settings-type');

            return settings;
        } catch (e) {
            console.warn(
                `Could not parse client settings from browser storage.`,
                {
                    value,
                    storageKey,
                    e,
                }
            );

            if (strict) {
                throw e;
            } else {
                return {};
            }
        }
    } else {
        return {};
    }
}

function setResolvedStorageClientSettings(
    storage: Storage,
    settings: PartialPreconClientSettings,
    storageKey = PRECON_CLIENT_SETTINGS_STORAGE_KEY
): PartialPreconClientSettings {
    const value = JSON.stringify(settings);
    storage.setItem(storageKey, value);
    return settings;
}

export function clearStorageClientSettings(
    storage: Storage,
    storageKey = PRECON_CLIENT_SETTINGS_STORAGE_KEY
): PartialPreconClientSettings {
    storage.removeItem(storageKey);
    return {};
}

function addResolvedStorageClientSettings(
    storage: Storage,
    settings: PartialPreconClientSettings,
    storageKey = PRECON_CLIENT_SETTINGS_STORAGE_KEY,
    strict = false
): PartialPreconClientSettings {
    const previousValue = getStorageClientSettings(storage, storageKey, strict);
    const nextValue = mergeClientSettings([previousValue, settings]);
    setResolvedStorageClientSettings(storage, nextValue);
    return nextValue;
}

export function setStorageClientSettings(
    storage: Storage,
    settings: PreconClientSettingsJson | string | URL | null,
    storageKey = PRECON_CLIENT_SETTINGS_STORAGE_KEY,
    strict = false,
    maxDepth = 10
): PartialPreconClientSettings | Promise<PartialPreconClientSettings> {
    if (!settings) return clearStorageClientSettings(storage, storageKey);

    if (typeof settings === 'string' || settings instanceof URL) {
        return getSettingsJson(settings, strict, maxDepth).then(
            (resolvedSettings) =>
                setResolvedStorageClientSettings(
                    storage,
                    resolvedSettings,
                    storageKey
                )
        );
    }

    if (settings.extends && settings.extends.length > 0) {
        return resolveSettingsJson(settings, strict, maxDepth).then(
            (resolvedSettings) =>
                setResolvedStorageClientSettings(
                    storage,
                    resolvedSettings,
                    storageKey
                )
        );
    }

    return setStorageClientSettings(storage, settings, storageKey);
}

export function addStorageClientSettings(
    storage: Storage,
    settings: PreconClientSettingsJson | string | URL,
    storageKey = PRECON_CLIENT_SETTINGS_STORAGE_KEY,
    strict = false,
    maxDepth = 10
): PartialPreconClientSettings | Promise<PartialPreconClientSettings> {
    if (!settings) return getStorageClientSettings(storage, storageKey, strict);

    if (typeof settings === 'string' || settings instanceof URL) {
        return getSettingsJson(settings, strict, maxDepth).then(
            (resolvedSettings) =>
                addResolvedStorageClientSettings(
                    storage,
                    resolvedSettings,
                    storageKey,
                    strict
                )
        );
    }

    if (settings.extends && settings.extends.length > 0) {
        return resolveSettingsJson(settings, strict, maxDepth).then(
            (resolvedSettings) =>
                addResolvedStorageClientSettings(
                    storage,
                    resolvedSettings,
                    storageKey,
                    strict
                )
        );
    }

    return setStorageClientSettings(storage, settings, storageKey);
}

export type GetStorageClientSettingsFn = (
    storageKey?: string,
    strict?: boolean
) => PartialPreconClientSettings;

export type ClearStorageClientSettingsFn = (
    storageKey?: string
) => PartialPreconClientSettings;

export type SetStorageClientSettingsFn = (
    settings: PreconClientSettingsJson | string | URL,
    storageKey?: string,
    strict?: boolean,
    maxDepth?: number
) => PartialPreconClientSettings | Promise<PartialPreconClientSettings>;

export type AddStorageClientSettingsFn = (
    settings: PreconClientSettingsJson | string | URL,
    storageKey?: string,
    strict?: boolean,
    maxDepth?: number
) => PartialPreconClientSettings | Promise<PartialPreconClientSettings>;

export function getLocalClientSettings(
    storageKey?: string,
    strict?: boolean
): PartialPreconClientSettings {
    if (typeof window !== 'undefined') {
        return getStorageClientSettings(
            window.localStorage,
            storageKey,
            strict
        );
    } else {
        return {};
    }
}

export function getSessionClientSettings(
    storageKey?: string,
    strict?: boolean
): PartialPreconClientSettings {
    if (typeof window !== 'undefined') {
        return getStorageClientSettings(
            window.sessionStorage,
            storageKey,
            strict
        );
    } else {
        return {};
    }
}

export function clearLocalClientSettings(
    storageKey?: string
): PartialPreconClientSettings {
    if (typeof window !== 'undefined') {
        return clearStorageClientSettings(window.localStorage, storageKey);
    } else {
        return {};
    }
}

export function clearSessionClientSettings(
    storageKey?: string
): PartialPreconClientSettings {
    if (typeof window !== 'undefined') {
        return clearStorageClientSettings(window.sessionStorage, storageKey);
    } else {
        return {};
    }
}

export function setLocalClientSettings(
    settings: PreconClientSettingsJson | string | URL,
    storageKey?: string,
    strict?: boolean,
    maxDepth?: number
): PartialPreconClientSettings | Promise<PartialPreconClientSettings> {
    if (typeof window !== 'undefined') {
        return setStorageClientSettings(
            window.localStorage,
            settings,
            storageKey,
            strict,
            maxDepth
        );
    } else {
        return {};
    }
}

export function setSessionClientSettings(
    settings: PreconClientSettingsJson | string | URL,
    storageKey?: string,
    strict?: boolean,
    maxDepth?: number
): PartialPreconClientSettings | Promise<PartialPreconClientSettings> {
    if (typeof window !== 'undefined') {
        return setStorageClientSettings(
            window.sessionStorage,
            settings,
            storageKey,
            strict,
            maxDepth
        );
    } else {
        return {};
    }
}

export function addLocalClientSettings(
    settings: PreconClientSettingsJson | string | URL,
    storageKey?: string,
    strict?: boolean,
    maxDepth?: number
): PartialPreconClientSettings | Promise<PartialPreconClientSettings> {
    if (typeof window !== 'undefined') {
        return addStorageClientSettings(
            window.localStorage,
            settings,
            storageKey,
            strict,
            maxDepth
        );
    } else {
        return {};
    }
}

export function addSessionClientSettings(
    settings: PreconClientSettingsJson | string | URL,
    storageKey?: string,
    strict?: boolean,
    maxDepth?: number
): PartialPreconClientSettings | Promise<PartialPreconClientSettings> {
    if (typeof window !== 'undefined') {
        return addStorageClientSettings(
            window.sessionStorage,
            settings,
            storageKey,
            strict,
            maxDepth
        );
    } else {
        return {};
    }
}

declare global {
    interface Window {
        getLocalClientSettings: GetStorageClientSettingsFn;
        getSessionClientSettings: GetStorageClientSettingsFn;
        clearLocalClientSettings: ClearStorageClientSettingsFn;
        clearSessionClientSettings: ClearStorageClientSettingsFn;
        setLocalClientSettings: SetStorageClientSettingsFn;
        setSessionClientSettings: SetStorageClientSettingsFn;
        addLocalClientSettings: AddStorageClientSettingsFn;
        addSessionClientSettings: AddStorageClientSettingsFn;
    }
}

if (typeof window !== 'undefined') {
    window.getSessionClientSettings = getSessionClientSettings;
    window.getLocalClientSettings = getLocalClientSettings;
    window.clearSessionClientSettings = clearSessionClientSettings;
    window.clearLocalClientSettings = clearLocalClientSettings;
    window.setSessionClientSettings = setSessionClientSettings;
    window.setLocalClientSettings = setLocalClientSettings;
    window.addSessionClientSettings = addSessionClientSettings;
    window.addLocalClientSettings = addLocalClientSettings;
}

export function getClientSettingsFromStorages(
    storageKeys: string | string[] = PRECON_CLIENT_SETTINGS_STORAGE_KEY,
    strict = false
): PartialPreconClientSettings {
    if (typeof storageKeys === 'string') storageKeys = [storageKeys];

    const settings = storageKeys.flatMap((storageKey) => [
        getLocalClientSettings(storageKey, strict),
        getSessionClientSettings(storageKey, strict),
    ]);

    return mergeClientSettings(settings);
}
